import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { ToastContainer, toast } from "react-toastify";
import AdminDetails from "../context/adminDetailsContext";
import RegistrationModal from "../Component/RegistrationModal";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { motion } from "framer-motion";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const navigate = useNavigate();
  const { setDetails, setSuperAdmin, setUserType } = useContext(AdminDetails);
  console.log(email, password);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // First try admin login (your existing endpoint)
      console.log("Attempting admin login...");
      const adminResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/adminAuth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );

      const adminData = await adminResponse.json();
      console.log("Admin Login Response:", adminData);

      // If admin login successful
      if (adminResponse.ok && adminData.data?.token) {
        setDetails(adminData.data);

        // Save admin ID to localStorage
        if (adminData.data?.admin?._id) {
          localStorage.setItem("adminId", adminData.data.admin._id);
        }

        Cookies.set("authToken", adminData.data.token, {
          expires: 1,
          secure: true,
          sameSite: "Strict",
        });

        toast.success("Login successful! Redirecting...", {
          position: "top-right",
        });

        setTimeout(() => {
          navigate("/home");
        }, 1500);

        return; // Exit early
      }

      // If admin login failed, try super admin
      console.log("Admin login failed, trying super admin...");
      
      const superAdminResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/superAdmin/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        }
      );

      const superAdminData = await superAdminResponse.json();
      console.log("Super Admin Login Response:", superAdminData);

      // Super admin login
      if (superAdminResponse.ok && superAdminData.data?.token) {
        console.log("✅ Super Admin login successful");

        // Clear any old tokens first
        Cookies.remove('authToken');

        // Set the super admin token
        Cookies.set("superAdminToken", superAdminData.data.token, {
          expires: 1,
          secure: true,
          sameSite: "Strict",
        });

        // Hard redirect immediately
        window.location.href = '/dashboard';
        return;
      }

      // Both logins failed
      throw new Error(adminData.message || superAdminData.message || "Invalid credentials");

    } catch (error) {
      console.error("Login Error:", error);
      toast.error(error.message || "An error occurred. Please try again later.", {
        position: "top-right",
      });
      setError(error.message || "An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    if (!resetEmail) {
      setResetError("Please enter your email.");
      return;
    }
    setResetLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/adminAuth/forgetPasswordSendOtp`,
        { email: resetEmail }
      );

      setOtpSent(true);
      toast.success("OTP sent to your email!");

    } catch (error) {
      setResetError(error.response?.data?.message || "Failed to send OTP.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    if (!otp || !newPassword) {
      setResetError("Please enter OTP and new password.");
      return;
    }
    setResetLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/apis/adminAuth/forgetPassword`,
        { email: resetEmail, otp, newPassword }
      );

      toast.success("Password reset successfully!");
      setShowModal(false);
      // Reset form
      setResetEmail("");
      setOtp("");
      setNewPassword("");
      setOtpSent(false);

    } catch (error) {
      setResetError(error.response?.data?.message || "Failed to reset password.");
    } finally {
      setResetLoading(false);
    }
  };

  // Animation variants
  const fadeInUp = {
    initial: { y: 50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const fadeInLeft = {
    initial: { x: -50, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const fadeInRight = {
    initial: { x: 50, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const scaleIn = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    transition: { duration: 0.5, ease: "easeOut" }
  };

  const rotateIn = {
    initial: { rotate: -10, opacity: 0 },
    animate: { rotate: 0, opacity: 1 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const bounceIn = {
    initial: { y: -100, opacity: 0 },
    animate: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const floatingAnimation = (delay = 0) => ({
    initial: { y: 0 },
    animate: {
      y: [-15, 15, -15],
      transition: {
        duration: 7,
        delay: delay,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  });

  const pulseAnimation = {
    initial: { scale: 1, opacity: 0.3 },
    animate: {
      scale: [1, 1.1, 1],
      opacity: [0.3, 0.5, 0.3],
      transition: {
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const rotateAnimation = {
    initial: { rotate: 0 },
    animate: {
      rotate: 360,
      transition: {
        duration: 20,
        repeat: Infinity,
        ease: "linear"
      }
    }
  };

  const waveAnimation = {
    initial: { x: -100, opacity: 0 },
    animate: {
      x: [-100, 100, -100],
      opacity: [0, 0.5, 0],
      transition: {
        duration: 15,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated Background Elements - Gray Theme - More of them */}
      <motion.div
        className="absolute top-10 left-10 w-72 h-72 bg-gray-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30"
        variants={pulseAnimation}
        initial="initial"
        animate="animate"
      />
      
      <motion.div
        className="absolute bottom-10 right-10 w-96 h-96 bg-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30"
        variants={{
          animate: {
            scale: [1, 1.3, 1],
            x: [0, -50, 0],
            y: [0, 30, 0],
            transition: {
              duration: 12,
              repeat: Infinity,
              repeatType: "reverse"
            }
          }
        }}
        initial="initial"
        animate="animate"
      />

      <motion.div
        className="absolute top-1/3 left-1/4 w-80 h-80 bg-gray-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
        variants={{
          animate: {
            scale: [1, 1.4, 1],
            x: [0, 70, 0],
            y: [0, -40, 0],
            transition: {
              duration: 14,
              repeat: Infinity,
              repeatType: "reverse"
            }
          }
        }}
        initial="initial"
        animate="animate"
      />

      <motion.div
        className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-gray-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
        variants={{
          animate: {
            scale: [1, 1.2, 1],
            rotate: [0, 10, -10, 0],
            transition: {
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse"
            }
          }
        }}
        initial="initial"
        animate="animate"
      />

      {/* Floating Particles */}
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-gray-400 rounded-full opacity-20"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, (i % 2 === 0 ? 20 : -20), 0],
            scale: [1, 1.5, 1],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 5 + i,
            repeat: Infinity,
            delay: i * 0.3,
            ease: "easeInOut"
          }}
        />
      ))}

      {/* Floating Icons - More of them */}
      <motion.div
        className="absolute top-40 left-20 text-gray-200 hidden lg:block"
        variants={floatingAnimation(0)}
        initial="initial"
        animate="animate"
      >
        <motion.svg 
          className="w-20 h-20" 
          fill="currentColor" 
          viewBox="0 0 24 24"
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
        </motion.svg>
      </motion.div>

      <motion.div
        className="absolute bottom-40 right-20 text-gray-200 hidden lg:block"
        variants={floatingAnimation(1)}
        initial="initial"
        animate="animate"
      >
        <motion.svg 
          className="w-24 h-24" 
          fill="currentColor" 
          viewBox="0 0 24 24"
          animate={{ rotate: -360 }}
          transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
        >
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h10v2H7zm0 4h6v2H7z"/>
        </motion.svg>
      </motion.div>

      <motion.div
        className="absolute top-60 right-40 text-gray-200 hidden lg:block"
        variants={floatingAnimation(2)}
        initial="initial"
        animate="animate"
      >
        <motion.svg 
          className="w-16 h-16" 
          fill="currentColor" 
          viewBox="0 0 24 24"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 2h6v2h-6V6zm0 4h6v2h-6v-2zm0 4h6v2h-6v-2zM8 6h2v2H8V6zm0 4h2v2H8v-2zm0 4h2v2H8v-2z"/>
        </motion.svg>
      </motion.div>

      <motion.div
        className="absolute bottom-60 left-40 text-gray-200 hidden lg:block"
        variants={floatingAnimation(3)}
        initial="initial"
        animate="animate"
      >
        <motion.svg 
          className="w-20 h-20" 
          fill="currentColor" 
          viewBox="0 0 24 24"
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        >
          <path d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10zM8 10h8v2H8v-2z"/>
        </motion.svg>
      </motion.div>

      {/* Wave Lines */}
      <motion.div
        className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-gray-200 to-transparent opacity-30"
        variants={waveAnimation}
        initial="initial"
        animate="animate"
      />

      <motion.div
        className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-gray-200 to-transparent rounded-full filter blur-3xl opacity-20"
        animate={{
          rotate: [0, 90, 180, 270, 360],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
      
      {/* Login Card with Multiple Animations */}
      <motion.div
        variants={bounceIn}
        initial="initial"
        animate="animate"
        whileHover={{ scale: 1.02, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
        className="bg-white p-8 rounded-xl shadow-lg w-full max-w-sm border border-gray-200 relative z-10"
      >
        <motion.h2 
          variants={fadeInLeft}
          initial="initial"
          animate="animate"
          className="text-2xl font-semibold text-left mb-2 text-gray-800"
        >
          <motion.span
            animate={{ color: ["#1F2937", "#4B5563", "#1F2937"] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            Welcome Back
          </motion.span>
        </motion.h2>
        
        <motion.p 
          variants={fadeInRight}
          initial="initial"
          animate="animate"
          className="text-left text-gray-600 mb-6"
        >
          Please enter your login credentials
        </motion.p>

        <motion.form 
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          onSubmit={handleLogin}
        >
          <motion.div 
            variants={fadeInUp}
            whileHover={{ x: 5 }}
            className="mb-4"
          >
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Email
            </label>
            <motion.input
              whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(156, 163, 175, 0.3)" }}
              whileHover={{ scale: 1.01 }}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700 transition-all"
              placeholder="Enter your email"
              required
            />
          </motion.div>
          
          <motion.div 
            variants={fadeInUp}
            whileHover={{ x: 5 }}
            className="mb-4"
          >
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Password
            </label>
            <motion.input
              whileFocus={{ scale: 1.02, boxShadow: "0 0 0 3px rgba(156, 163, 175, 0.3)" }}
              whileHover={{ scale: 1.01 }}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700 transition-all"
              placeholder="Enter your password"
              required
            />
          </motion.div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="text-red-500 text-sm mb-4 p-2 bg-red-50 rounded-lg border border-red-200"
            >
              {error}
            </motion.div>
          )}

          <motion.button
            variants={scaleIn}
            whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2)" }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-900 focus:outline-none transition-all duration-200 font-medium relative overflow-hidden"
            disabled={loading}
          >
            <motion.span
              animate={loading ? { x: [-100, 100] } : {}}
              transition={{ duration: 1, repeat: Infinity }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            />
            {loading ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full"
              />
            ) : (
              "Login"
            )}
          </motion.button>

          <motion.div 
            variants={fadeInUp}
            className="text-center mt-4"
          >
            <motion.button
              whileHover={{ scale: 1.05, color: "#1F2937" }}
              whileTap={{ scale: 0.95 }}
              type="button"
              className="text-gray-600 hover:text-gray-800 hover:underline text-sm transition-all"
              onClick={() => setShowModal(true)}
            >
              Forgot Password?
            </motion.button>
          </motion.div>

          {/* New User Registration Link */}
          <motion.div 
            variants={fadeInUp}
            className="text-center mt-6 pt-4 border-t border-gray-200"
          >
            <p className="text-gray-600 text-sm">
              New College?{" "}
              <motion.button
                whileHover={{ scale: 1.05, color: "#1F2937" }}
                whileTap={{ scale: 0.95 }}
                animate={{ 
                  textShadow: ["0 0 0 rgba(0,0,0,0)", "0 0 4px rgba(75,85,99,0.3)", "0 0 0 rgba(0,0,0,0)"]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                type="button"
                onClick={() => setShowRegistrationModal(true)}
                className="text-gray-800 font-semibold hover:underline"
              >
                Create Account
              </motion.button>
            </p>
          </motion.div>
        </motion.form>

        {/* Decorative Elements on Card */}
        <motion.div
          className="absolute -top-2 -right-2 w-20 h-20 bg-gray-200 rounded-full opacity-20 blur-xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
          }}
        />
        
        <motion.div
          className="absolute -bottom-2 -left-2 w-16 h-16 bg-gray-300 rounded-full opacity-20 blur-xl"
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            delay: 1,
          }}
        />
      </motion.div>

      {/* Password Reset Modal */}
      {showModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowModal(false);
            setResetEmail("");
            setOtpSent(false);
          }}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, rotate: -5 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            exit={{ scale: 0.8, opacity: 0, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.h3 
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xl font-semibold mb-4 text-gray-800"
            >
              Reset Your Password
            </motion.h3>

            {!otpSent ? (
              <form onSubmit={handleForgetPassword}>
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-4"
                >
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Enter your email address
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
                    required
                  />
                </motion.div>
                {resetError && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-sm mb-4"
                  >
                    {resetError}
                  </motion.div>
                )}
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="flex gap-2"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setResetEmail("");
                      setOtpSent(false);
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-900 transition-colors duration-200 font-medium"
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Sending..." : "Send OTP"}
                  </button>
                </motion.div>
              </form>
            ) : (
              <form onSubmit={handleResetPassword}>
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mb-4"
                >
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    OTP
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
                    required
                  />
                </motion.div>
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-4"
                >
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
                    required
                  />
                </motion.div>
                {resetError && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-500 text-sm mb-4"
                  >
                    {resetError}
                  </motion.div>
                )}
                <motion.div 
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="flex gap-2"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setResetEmail("");
                      setOtp("");
                      setNewPassword("");
                      setOtpSent(false);
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-900 transition-colors duration-200 font-medium"
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Resetting..." : "Reset Password"}
                  </button>
                </motion.div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
      />
    </div>
  );
};

export default Login;























// import React, { useState, useContext } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import Cookies from "js-cookie";
// import { ToastContainer, toast } from "react-toastify";
// import AdminDetails from "../context/adminDetailsContext";
// import "react-toastify/dist/ReactToastify.css";

// const Login = () => {
//   const [email, setEmail] = useState("");
//   const [password, setPassword] = useState("");
//   const [error, setError] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [showModal, setShowModal] = useState(false);
//   const [resetEmail, setResetEmail] = useState("");
//   const [resetError, setResetError] = useState("");
//   const [otp, setOtp] = useState("");
//   const [newPassword, setNewPassword] = useState("");
//   const [otpSent, setOtpSent] = useState(false);
//   const [resetLoading, setResetLoading] = useState(false);
//   const navigate = useNavigate();
//   const { details, setDetails } = useContext(AdminDetails);

// const handleLogin = async (e) => {
//   e.preventDefault();
//   setLoading(true);
//   setError("");
//   try {
//     const response = await fetch(
//       `${process.env.REACT_APP_BACKEND_URL}/apis/adminAuth/login`,
//       {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ email, password }),
//       }
//     );
//     const data = await response.json();
//     console.log("Login Response:", data);

//     setDetails(data.data);

//     // ✅ Save admin ID to localStorage
//     if (data.data?.admin?._id) {
//       localStorage.setItem("adminId", data.data.admin._id);
//     }

//     if (data.data?.token) {
//       Cookies.set("authToken", data.data.token, {
//         expires: 1,
//         secure: true,
//         sameSite: "Strict",
//       });

//       toast.success("Login successful! Redirecting...", {
//         position: "top-right",
//       });

//       setTimeout(() => {
//         navigate("/home");
//       }, 1500);
//     } else {
//       toast.error(data.message || "Invalid credentials!", {
//         position: "top-right",
//       });
//     }
//   } catch (error) {
//     console.error("Login Error:", error);
//     toast.error("An error occurred. Please try again later.", {
//       position: "top-right",
//     });
//     setError("An error occurred. Please try again later.");
//   } finally {
//     setLoading(false);
//   }
// };

//   const handleForgetPassword = async (e) => {
//     e.preventDefault();
//     setResetError("");
//     if (!resetEmail) {
//       setResetError("Please enter your email.");
//       return;
//     }
//     setResetLoading(true);
//     try {
//       const response = await fetch(
//         `${process.env.REACT_APP_BACKEND_URL}/apis/adminAuth/forgetPasswordSendOtp`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({ email: resetEmail }),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         setOtpSent(true);
//         alert("OTP has been sent to your email.");
//       } else {
//         setResetError(data.message || "Failed to send OTP.");
//       }
//     } catch (error) {
//       setResetError("An error occurred. Please try again later.");
//     } finally {
//       setResetLoading(false);
//     }
//   };

//   const handleResetPassword = async (e) => {
//     e.preventDefault();
//     setResetError("");
//     if (!otp || !newPassword) {
//       setResetError("Please enter OTP and new password.");
//       return;
//     }
//     setResetLoading(true);
//     try {
//       const response = await fetch(
//         `${process.env.REACT_APP_BACKEND_URL}/apis/adminAuth/forgetPassword`,
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({ email: resetEmail, otp, newPassword }),
//         }
//       );
//       const data = await response.json();
//       if (response.ok) {
//         alert("Password reset successfully.");
//         setShowModal(false);
//       } else {
//         setResetError(data.message || "Failed to reset password.");
//       }
//     } catch (error) {
//       setResetError("An error occurred. Please try again later.");
//     } finally {
//       setResetLoading(false);
//     }
//   };

//   return (
//     <div className="flex items-center justify-center min-h-screen bg-gray-100">
//       <ToastContainer
//         position="top-right"
//         autoClose={3000}
//         hideProgressBar={false}
//       />
//       <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-sm">
//         <h2 className="text-2xl font-semibold text-left mb-2">Login</h2>
//         <p className="text-left text-gray-600 mb-6">
//           Please enter your login credentials
//         </p>
//         <form onSubmit={handleLogin}>
//           <div className="mb-4">
//             <label className="block text-gray-700 text-sm font-bold mb-2">
//               User Name
//             </label>
//             <input
//               type="email"
//               value={email}
//               onChange={(e) => setEmail(e.target.value)}
//               className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//               required
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-700 text-sm font-bold mb-2">
//               Password
//             </label>
//             <input
//               type="password"
//               value={password}
//               onChange={(e) => setPassword(e.target.value)}
//               className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//               required
//             />
//           </div>
//           {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
//           <button
//             type="submit"
//             className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:bg-blue-600"
//             disabled={loading}
//           >
//             {loading ? "Logging in..." : "Login"}
//           </button>
//           <div className="text-center mt-4">
//             <Link
//               to="#"
//               className="text-blue-500 hover:underline"
//               onClick={() => setShowModal(true)}
//             >
//               Forgot Password?
//             </Link>
//           </div>
//         </form>
//       </div>

//       {/* Password Reset Modal */}
//       {showModal && (
//         <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
//             <h3 className="text-xl font-semibold mb-4">Reset Your Password</h3>
//             {!otpSent ? (
//               <form onSubmit={handleForgetPassword}>
//                 <div className="mb-4">
//                   <label className="block text-gray-700 text-sm font-bold mb-2">
//                     Enter your email address
//                   </label>
//                   <input
//                     type="email"
//                     value={resetEmail}
//                     onChange={(e) => setResetEmail(e.target.value)}
//                     className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     required
//                   />
//                 </div>
//                 {resetError && (
//                   <div className="text-red-500 text-sm mb-4">{resetError}</div>
//                 )}
//                 <button
//                   type="submit"
//                   className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:bg-blue-600"
//                   disabled={resetLoading}
//                 >
//                   {resetLoading ? "Sending OTP..." : "Send OTP"}
//                 </button>
//               </form>
//             ) : (
//               <form onSubmit={handleResetPassword}>
//                 <div className="mb-4">
//                   <label className="block text-gray-700 text-sm font-bold mb-2">
//                     OTP
//                   </label>
//                   <input
//                     type="text"
//                     value={otp}
//                     onChange={(e) => setOtp(e.target.value)}
//                     className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     required
//                   />
//                 </div>
//                 <div className="mb-4">
//                   <label className="block text-gray-700 text-sm font-bold mb-2">
//                     New Password
//                   </label>
//                   <input
//                     type="password"
//                     value={newPassword}
//                     onChange={(e) => setNewPassword(e.target.value)}
//                     className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
//                     required
//                   />
//                 </div>
//                 {resetError && (
//                   <div className="text-red-500 text-sm mb-4">{resetError}</div>
//                 )}
//                 <button
//                   type="submit"
//                   className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:bg-blue-600"
//                   disabled={resetLoading}
//                 >
//                   {resetLoading ? "Resetting Password..." : "Reset Password"}
//                 </button>
//               </form>
//             )}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Login;
